<script setup>
import HeroSection from '@/components/pages/HeroSection.vue';
import FordButton from '@/components/FordButton.vue';
import { computed, onMounted, watch } from 'vue';
import { setSocialMetaTag, updateSocialMetaTag } from '@/utils/setMetaTags';
import { formatRange } from '@/utils/date';
import dayjs from '@/utils/dayjs';
import EventsFilter from './EventsFilter.vue';
import EventCards from './EventCards.vue';
import NoEventsMsg from './NoEventsMsg.vue';

const props = defineProps({
  eventTypes: Array,
  orderings: Array,
  solutions: Array,
  inputs: Object,
  events: Array,
  featuredEvent: Object, // a normal event with even.featured === true
  apiStillLoading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['filter', 'reset']);

const filter = (options) => emit('filter', options);

/* below is for "featured" event Hero Section logic */

function heroButtonPropertyHandler(event) {
  const type = event.typeEvent?.name;
  const { thirdPartyOwned } = event;

  if (thirdPartyOwned) {
    return {
      btnText: 'View Event',
      btnWidth: 185,
    };
  }

  if (type === 'VIRTUAL') {
    return {
      btnText: 'View & Register for Livestream',
      btnWidth: 285,
    };
  }

  if (type === 'IN-PERSON' || type === 'IN-PERSON/VIRTUAL') {
    return {
      btnText: 'View & Register for Event',
      btnWidth: 250,
    };
  }

  if (type === 'ON-DEMAND') {
    return {
      btnText: 'Watch Now',
      btnWidth: 185,
    };
  }

  return '';
}

function heroDateCreator(event) {
  const type = event.typeEvent?.name;

  if (type === 'VIRTUAL' || type === 'IN-PERSON/VIRTUAL') {
    return dayjs(event.startDateTime).format('MMM D, YYYY');
  }

  if (type === 'IN-PERSON') {
    return formatRange(event.startDateTime)(event.endDateTime);
  }

  return '';
}

const mappedHeroSection = computed(() => {
  const eventsAreLoaded = !props.apiStillLoading;
  const featured = props.featuredEvent;

  // featured event found
  if (eventsAreLoaded && props.featuredEvent) {
    const featuredContent = {
      id: featured.id,
      title: featured.name,
      subTitle: featured.description,
      url: `${process.env.VUE_APP_ASSET_PATH}${featured.assetForWaitingRoom?.fileName}`,
      header: featured.name,
      buttonProperties: heroButtonPropertyHandler(featured),
      alt: featured.assetAltTextWaitingRoom,
    };
    return featuredContent;
  }

  // no featured event
  if (eventsAreLoaded) {
    return {
      title: 'FORD PRO<sup>&trade;</sup> EVENTS',
      subTitle: `
        Planning to visit a fleet trade show? We’d love to talk to you
        about how Ford Pro can accelerate your productivity. See below
        to browse upcoming events and schedule a one-on-one appointment
        with a Ford Pro expert.
      `,
      url: require('@/assets/img/Hero-Home.jpg'),
    };
  }

  // still loading
  return {};
});

/**
 * Meta tags control
 */
onMounted(() => {
  setSocialMetaTag('og:title');
  setSocialMetaTag('og:description');
  setSocialMetaTag('og:image');
});
watch(mappedHeroSection, () => {
  updateSocialMetaTag('og:title', mappedHeroSection.value.title);
  updateSocialMetaTag('og:description', mappedHeroSection.value.subTitle);
  updateSocialMetaTag('og:image', mappedHeroSection.value.url);
});
</script>
<template>
  <div>
    <hero-section v-bind="mappedHeroSection" :centered="!featuredEvent">
      <template #details>
        <div v-if="featuredEvent" class="mb-10">
          {{ heroDateCreator(featuredEvent) }}
        </div>
      </template>
      <template #button>
        <a v-if="featuredEvent" :href="`event/${mappedHeroSection.id}`">
          <ford-button
            invert
            :minWidth="`${mappedHeroSection.buttonProperties.btnWidth}`"
            class="opacity-[.85]"
            :prevent-default="false"
          >
            {{ mappedHeroSection.buttonProperties.btnText }}
          </ford-button>
        </a>
      </template>
    </hero-section>
    <events-filter
      :eventTypes="eventTypes"
      :orderings="orderings"
      :solutions="solutions"
      :inputs="inputs"
      @filter="filter"
    ></events-filter>
    <event-cards v-if="events.length >= 1" :events="events"></event-cards>
    <no-events-msg @reset="emit('reset')" v-else></no-events-msg>
  </div>
</template>
